import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { resetPasswordAPI } from "../../api/auth";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";
import { openModal } from "../../redux/slices/uiSlice";
import { LENGTH, REGEX } from "../../utils/constant";

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: resetPasswordAPI,
    onSuccess: (data: { message?: string }) => {
      useToast(data?.message || t("resetPassword.save") || "Password reset successfully.", "success");
      dispatch(openModal("login"));
      navigate("/");
    },
    onError: (error: Error) => {
      useToast(error.message || "Failed to reset password.", "error");
    },
  });

  const { values, handleChange, handleBlur, handleSubmit, touched, errors } = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .trim()
        .required(t("validation.passwordRequired"))
        .matches(REGEX.START_END_SPACE, t("validation.passwordNoSpaces"))
        .min(LENGTH.MIN_PASSWORD_LENGTH, t("validation.passwordMin"))
        .max(LENGTH.MAX_PASSWORD_LENGTH, t("validation.passwordMax")),
      confirmPassword: Yup.string()
        .trim()
        .required(t("validation.confirmPasswordRequired"))
        .oneOf([Yup.ref("password"), null], t("resetPassword.passwordMismatch")),
    }),
    onSubmit: (values) => {
      if (!token) {
        useToast("Invalid or missing reset link.", "error");
        return;
      }
      mutate({
        token,
        new_password: values.password,
      });
    },
  });

  const inputBaseClass =
    "2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent";
  const inputWrapperClass = (hasError: boolean) =>
    `relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all ${
      hasError
        ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
        : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
    }`;
  const errorTextClass =
    "text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold";
  const eyeButtonClass =
    "absolute lg:end-[1vw] end-5 top-1/2 -translate-y-1/2 text-black-400 hover:text-black-300 transition-colors";

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-ivory lg:p-[2vw] p-4">
        <div className="bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 2xl:max-w-[26vw] max-w-100 w-full text-center">
          <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.6vw] mb-3">
            {t("resetPassword.resetPassword")}
          </h1>
          <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold lg:mb-[1.1vw] mb-4">
            Invalid or missing reset link. Please request a new password reset.
          </p>
          <button
            type="button"
            onClick={() => {
              dispatch(openModal("login"));
              navigate("/");
            }}
            className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          >
            <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
              <span className="skew-x-[10deg] block">{t("register.login")}</span>
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-ivory lg:p-[2vw] p-4">
      <div className="bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 2xl:max-w-[26vw] max-w-100 w-full">
        <div className="text-center lg:mb-[1.2vw] mb-4">
          <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
            {t("resetPassword.resetPassword")}
          </h1>
          <p className="lg:text-[1.1vw]/tight text-base/tight text-black-800 font-bold">
            {t("resetPassword.enterNewPassword")}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1">
            <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
              <div className={inputWrapperClass(Boolean(touched.password && errors.password))}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("resetPassword.newPassword")}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isPending}
                  className={inputBaseClass}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={eyeButtonClass}
                  aria-label={showPassword ? t("register.hidePassword") : t("register.showPassword")}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {touched.password && errors.password && (
                <p className={errorTextClass}>{errors.password}</p>
              )}
            </div>

            <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
              <div
                className={inputWrapperClass(Boolean(touched.confirmPassword && errors.confirmPassword))}
              >
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder={t("resetPassword.reEnterPassword")}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isPending}
                  className={inputBaseClass}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={eyeButtonClass}
                  aria-label={
                    showConfirmPassword ? t("register.hidePassword") : t("register.showPassword")
                  }
                >
                  {showConfirmPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className={errorTextClass}>{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isPending}
              className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
                <span className="skew-x-[10deg] block">
                  {isPending ? t("resetPassword.saving") : t("resetPassword.save")}
                </span>
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
